import { DatePicker } from "@/components/ui/datepicker";
import { routes } from "@/config/routes";
import {
  ICreatePushNotificationDTO,
  IPushNotification,
} from "@/utilities/types/PushNotification";
import {
  createPushNotificationSchema,
  PUSH_NOTIFICATION_LIMITS,
} from "@/utilities/validators/push-notification";
import cn from "@/utils/class-names";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { PiArrowLeftBold, PiPaperPlaneTiltBold } from "react-icons/pi";
import { useNavigate } from "react-router-dom";
import { Button, Input, Radio, RadioGroup, Textarea, Title } from "rizzui";
import ConfirmBlastModal from "./confirm-blast-modal";

type ScheduleMode = "immediate" | "scheduled";

const { TITLE_MAX, BODY_MAX, URL_MAX } = PUSH_NOTIFICATION_LIMITS;

export interface PushNotificationFormProps {
  initialData?: IPushNotification | null;
  isLoading?: boolean;
  isSaving?: boolean;
  onCreateDraft: (
    data: ICreatePushNotificationDTO,
  ) => Promise<IPushNotification>;
  onUpdateDraft: (
    id: string,
    data: ICreatePushNotificationDTO,
  ) => Promise<IPushNotification>;
  onSaveDraft: (data: ICreatePushNotificationDTO) => Promise<IPushNotification>;
  onSendNow: (blastId: string) => Promise<void>;
  onSchedule: (blastId: string, scheduledAt: Date) => Promise<void>;
  onCancelScheduled?: (id: string) => Promise<void>;
  isUpdate?: boolean;
}

const defaultValues: ICreatePushNotificationDTO = {
  title: "",
  body: "",
  deepLink: undefined,
  imageUrl: undefined,
};

export default function PushNotificationForm({
  initialData,
  isLoading,
  isSaving = false,
  onCreateDraft,
  onUpdateDraft,
  onSaveDraft,
  onSendNow,
  onSchedule,
  onCancelScheduled,
  isUpdate = false,
}: PushNotificationFormProps) {
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [scheduleMode, setScheduleMode] = useState<ScheduleMode>("immediate");
  const [scheduledAt, setScheduledAt] = useState<Date | null>(null);
  const [createdBlastId, setCreatedBlastId] = useState<string | null>(
    initialData?.id ?? null,
  );

  const form = useForm<ICreatePushNotificationDTO>({
    resolver: zodResolver(createPushNotificationSchema),
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = form;

  const titleValue = watch("title") ?? "";
  const bodyValue = watch("body") ?? "";
  const deepLinkValue = watch("deepLink") ?? "";
  const imageUrlValue = watch("imageUrl") ?? "";
  const previewTitle = titleValue.trim() || "Notification title";
  const previewBody =
    bodyValue.trim() || "Your message preview will appear here.";
  const previewTime = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date());

  useEffect(() => {
    if (initialData) {
      reset({
        title: initialData.title ?? "",
        body: initialData.body ?? "",
        deepLink: initialData.deepLink ?? undefined,
        imageUrl: initialData.imageUrl ?? undefined,
      });
      setCreatedBlastId(initialData.id);
      if (initialData.scheduledAt) {
        setScheduleMode("scheduled");
        setScheduledAt(new Date(initialData.scheduledAt));
      }
    }
  }, [initialData, reset]);

  const collectFormData = (): ICreatePushNotificationDTO => ({
    title: titleValue,
    body: bodyValue,
    deepLink: deepLinkValue || undefined,
    imageUrl: imageUrlValue || undefined,
  });

  const canSchedule =
    scheduleMode === "scheduled" && scheduledAt instanceof Date;

  const handleSaveAsDraft = handleSubmit(async (data) => {
    const result = await onSaveDraft(data);
    setCreatedBlastId(result.id);
  });

  // Open confirm modal. Make sure the latest form values are persisted to the
  // server first:
  //  - Update mode: always PATCH the existing blast so user edits are applied.
  //  - Create mode (draft already exists from Save-as-Draft): no-op.
  //  - Create mode (no blast yet): POST a new draft silently (no redirect),
  //    then open the modal.
  const handleSendClick = handleSubmit(async (data) => {
    try {
      let blastId = createdBlastId;
      if (blastId && isUpdate) {
        await onUpdateDraft(blastId, data);
      } else if (!blastId) {
        const result = await onCreateDraft(data);
        blastId = result.id;
        setCreatedBlastId(blastId);
      }
      setConfirmOpen(true);
    } catch {
      // toast handled by caller
    }
  });

  const handleConfirm = async () => {
    if (!createdBlastId) return;
    try {
      if (scheduleMode === "immediate") {
        await onSendNow(createdBlastId);
      } else if (scheduledAt) {
        await onSchedule(createdBlastId, scheduledAt);
      }
      setConfirmOpen(false);
      navigate(routes.pushNotification.listing);
    } catch {
      // toast handled by caller
    }
  };

  return (
    <div className="@container">
      <div className="mb-6 flex items-center justify-between">
        <Title as="h4" className="mb-1">
          {isUpdate ? "Edit Push Notification" : "New Push Notification"}
        </Title>

        <Button
          variant="outline"
          onClick={() => navigate(routes.pushNotification.listing)}
          className="gap-2"
        >
          <PiArrowLeftBold className="h-4 w-4" />
          Back
        </Button>
      </div>

      <form
        className="grid grid-cols-1 gap-6 lg:grid-cols-3"
        onSubmit={(e) => e.preventDefault()}
      >
        <div className="space-y-6 lg:col-span-2">
          {/* Content section */}
          <section className="rounded-md border border-muted bg-white p-5">
            <Title as="h6" className="mb-4">
              Content
            </Title>

            <div className="space-y-4">
              <Controller
                control={control}
                name="title"
                render={({ field }) => (
                  <div>
                    <div className="mb-1 flex items-baseline justify-between">
                      <label className="text-sm font-medium">
                        Notification title
                      </label>
                      <span
                        className={cn(
                          "text-xs",
                          titleValue.length > TITLE_MAX
                            ? "text-red-600"
                            : "text-gray-400",
                        )}
                      >
                        {titleValue.length} / {TITLE_MAX}
                      </span>
                    </div>
                    <Input
                      {...field}
                      placeholder="Keep it short so it fits in the notification"
                      maxLength={TITLE_MAX}
                      error={errors.title?.message}
                      clearable
                      onClear={() => field.onChange("")}
                    />
                    <p className="mt-1 text-xs text-gray-400">
                      Recommended limit: keep the title within {TITLE_MAX}{" "}
                      characters so it is more likely to appear in full.
                    </p>
                  </div>
                )}
              />

              <Controller
                control={control}
                name="body"
                render={({ field }) => (
                  <div>
                    <div className="mb-1 flex items-baseline justify-between">
                      <label className="text-sm font-medium">Message</label>
                      <span
                        className={cn(
                          "text-xs",
                          bodyValue.length > BODY_MAX
                            ? "text-red-600"
                            : "text-gray-400",
                        )}
                      >
                        {bodyValue.length} / {BODY_MAX}
                      </span>
                    </div>
                    <Textarea
                      {...field}
                      placeholder="Write a short message that should appear fully in the notification"
                      rows={4}
                      maxLength={BODY_MAX}
                      error={errors.body?.message}
                    />
                    <p className="mt-1 text-xs text-gray-400">
                      Recommended limit: keep the message within {BODY_MAX}{" "}
                      characters for better full-message visibility across
                      devices.
                    </p>
                  </div>
                )}
              />

              {/* Deep link (optional) */}
              <div>
                <label className="mb-1 block text-sm font-medium">
                  Deep link
                </label>
                <Controller
                  control={control}
                  name="deepLink"
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      placeholder="https://example.com"
                      error={errors.deepLink?.message}
                      maxLength={URL_MAX + 10}
                    />
                  )}
                />
                <p className="mt-1 text-xs text-gray-400">
                  Optional. Tapping the notification will open this URL or app
                  deep link.
                </p>
              </div>
            </div>
          </section>

          {/* Schedule section */}
          <section className="rounded-md border border-muted bg-white p-5">
            <Title as="h6" className="mb-4">
              Schedule
            </Title>

            <RadioGroup
              value={scheduleMode}
              setValue={(v) => setScheduleMode(v as ScheduleMode)}
              className="grid grid-cols-1 gap-2 md:grid-cols-2"
            >
              <Radio
                name="schedule"
                value="immediate"
                label="Send immediately"
              />
              <Radio
                name="schedule"
                value="scheduled"
                label="Schedule for later"
              />
            </RadioGroup>

            {scheduleMode === "scheduled" && (
              <div className="mt-4">
                <label className="mb-1 block text-sm font-medium">
                  Scheduled time
                </label>
                <DatePicker
                  selected={scheduledAt}
                  onChange={(d: Date | null) => setScheduledAt(d)}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="yyyy-MM-dd HH:mm"
                  minDate={new Date()}
                  placeholderText="Pick a date and time"
                />
                {!canSchedule && (
                  <p className="mt-1 text-xs text-yellow-700">
                    Please select a schedule time to enable scheduling.
                  </p>
                )}
              </div>
            )}
          </section>
        </div>

        {/* Sidebar actions */}
        <aside className="space-y-4 lg:col-span-1">
          <section className="rounded-md border border-muted bg-white p-5">
            <Title as="h6" className="mb-3">
              Notification Preview
            </Title>
            <div className="rounded-[28px] bg-slate-900 p-3 text-white">
              <div className="rounded-[22px] bg-[linear-gradient(180deg,#1e293b_0%,#0f172a_100%)] p-3 shadow-inner">
                <div className="mb-3 flex items-center justify-between text-[11px] text-slate-300">
                  <span>Notification Center</span>
                  <span>{previewTime}</span>
                </div>
                <div className="rounded-2xl bg-white/12 p-3 backdrop-blur">
                  <div className="flex items-start gap-3">
                    <img
                      src="/pwa-128x128.png"
                      alt="App icon"
                      className="mt-0.5 h-8 w-8 shrink-0 rounded-full bg-white/15 object-cover"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="truncate text-xs font-semibold uppercase tracking-[0.2em] text-slate-200">
                          ProjectSG Dashboard
                        </p>
                        <span className="shrink-0 text-[11px] text-slate-300">
                          now
                        </span>
                      </div>
                      <p className="mt-1 break-words text-sm font-semibold leading-5 text-white">
                        {previewTitle}
                      </p>
                      <p className="mt-1 break-words text-sm leading-5 text-slate-200">
                        {previewBody}
                      </p>
                      {imageUrlValue ? (
                        <img
                          src={imageUrlValue}
                          alt="Notification preview"
                          className="mt-3 max-h-32 w-full rounded-xl object-cover"
                          onError={(e) => {
                            (
                              e.currentTarget as HTMLImageElement
                            ).style.display = "none";
                          }}
                        />
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-400">
              Preview only. Actual layout may vary by device, OS, and whether
              the notification is expanded.
            </p>
            {deepLinkValue ? (
              <p className="mt-2 break-all text-xs text-gray-500">
                Opens: {deepLinkValue}
              </p>
            ) : null}
          </section>

          <div className="sticky top-4 rounded-md border border-muted bg-white p-5">
            <Title as="h6" className="mb-3">
              Actions
            </Title>
            <div className="space-y-2">
              {scheduleMode === "immediate" && (
                <Button
                  type="button"
                  onClick={handleSaveAsDraft}
                  variant="outline"
                  className="w-full"
                  isLoading={isSaving}
                  disabled={isLoading || isSaving}
                >
                  Save as Draft
                </Button>
              )}
              <Button
                type="button"
                onClick={handleSendClick}
                className="w-full"
                isLoading={isSaving}
                disabled={
                  isLoading ||
                  isSaving ||
                  (scheduleMode === "scheduled" && !canSchedule)
                }
              >
                <PiPaperPlaneTiltBold className="me-1.5 h-4 w-4" />
                {scheduleMode === "immediate" ? "Send Now" : "Schedule"}
              </Button>
              {isUpdate && onCancelScheduled && initialData && (
                <Button
                  type="button"
                  variant="flat"
                  className="w-full"
                  onClick={() => onCancelScheduled(initialData.id)}
                >
                  Cancel scheduled
                </Button>
              )}
            </div>
            {createdBlastId && isDirty && (
              <p className="mt-3 text-xs text-yellow-700">
                You have unsaved changes. They will be saved when you send.
              </p>
            )}
          </div>
        </aside>
      </form>

      <ConfirmBlastModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        data={collectFormData()}
        scheduleMode={scheduleMode}
        scheduledAt={scheduledAt}
        isLoading={isSaving}
      />
    </div>
  );
}
